import { useState, useEffect } from 'react'
import { X, Tag, DollarSign, Trash2, Edit2 } from 'lucide-react'

interface ListedSlideoutProps {
  isOpen: boolean
  onClose: () => void
  itemId: string
  itemName: string
  onSuccess?: () => void
}

interface Listing {
  id: string
  platform: string
  listing_id: string | null
  title: string | null
  price: string | null
  status: string
}

export default function ListedSlideout({ isOpen, onClose, itemId, itemName, onSuccess }: ListedSlideoutProps) {
  const [existingListings, setExistingListings] = useState<Listing[]>([])
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([])
  const [listingId, setListingId] = useState('')
  const [title, setTitle] = useState('')
  const [price, setPrice] = useState('')
  const [status, setStatus] = useState('active')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const platforms = ['eBay', 'Mercari', 'Poshmark', 'Facebook Marketplace', 'Amazon', 'Etsy', 'Shopify', 'Other']

  // Fetch existing listings when slideout opens
  useEffect(() => {
    if (isOpen) {
      fetchExistingListings()
    } else {
      // Reset form when closed
      setSelectedPlatforms([])
      setListingId('')
      setTitle('')
      setPrice('')
      setStatus('active')
      setError('')
    }
  }, [isOpen, itemId])

  const fetchExistingListings = async () => {
    try {
      const response = await fetch(`/api/listings-pg?item_id=${itemId}`)
      const data = await response.json()
      if (data.success) {
        setExistingListings(data.listings || [])
      }
    } catch (err) {
      console.error('Error fetching listings:', err)
    }
  }

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    )
  }

  const handleDeleteListing = async (listingId: string, platform: string) => {
    if (!confirm(`Remove ${platform} listing?`)) return

    try {
      const response = await fetch(`/api/listings-pg?id=${listingId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        setExistingListings(prev => prev.filter(l => l.id !== listingId))
        if (onSuccess) onSuccess()
      } else {
        setError('Failed to delete listing')
      }
    } catch (err) {
      setError('Failed to delete listing')
    }
  }

  const handleToggleStatus = async (listing: Listing) => {
    const newStatus = listing.status === 'active' ? 'ended' : 'active'

    try {
      const response = await fetch('/api/listings-pg', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: listing.id,
          listing_id: listing.listing_id,
          title: listing.title,
          price: listing.price,
          status: newStatus
        })
      })

      if (response.ok) {
        setExistingListings(prev =>
          prev.map(l => l.id === listing.id ? { ...l, status: newStatus } : l)
        )
        if (onSuccess) onSuccess()
      } else {
        setError('Failed to update listing')
      }
    } catch (err) {
      setError('Failed to update listing')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (selectedPlatforms.length === 0) {
      setError('Please select at least one marketplace')
      return
    }

    setLoading(true)
    setError('')

    try {
      // Create/update listing for each selected platform (upsert)
      const promises = selectedPlatforms.map(platform =>
        fetch('/api/listings-pg', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            item_id: itemId,
            platform,
            listing_id: listingId || null,
            title: title || itemName,
            price: price ? parseFloat(price) : null,
            status
          })
        })
      )

      const responses = await Promise.all(promises)

      // Check if all succeeded
      for (const response of responses) {
        if (!response.ok) {
          const data = await response.json()
          throw new Error(data.error || 'Failed to create/update listing')
        }
      }

      // Refresh listings and reset form
      await fetchExistingListings()
      setSelectedPlatforms([])
      setListingId('')
      setTitle('')
      setPrice('')
      setStatus('active')

      if (onSuccess) onSuccess()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  const existingPlatforms = existingListings.map(l => l.platform)
  const availablePlatforms = platforms.filter(p => !existingPlatforms.includes(p))

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9998,
          backdropFilter: 'blur(4px)'
        }}
        onClick={onClose}
      />

      {/* Slideout */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '550px',
          maxWidth: '90vw',
          background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
          boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '1px solid rgba(59, 130, 246, 0.3)'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
            background: 'rgba(59, 130, 246, 0.05)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.3)'
                }}
              >
                <Tag style={{ width: 20, height: 20, color: '#3b82f6' }} />
              </div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#93c5fd', margin: 0 }}>
                Marketplace Listings
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X style={{ width: 24, height: 24 }} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            padding: '24px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}
        >
          {error && (
            <div
              style={{
                padding: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '6px',
                color: '#ef4444',
                fontSize: '0.875rem'
              }}
            >
              {error}
            </div>
          )}

          {/* Existing Listings */}
          {existingListings.length > 0 && (
            <div>
              <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#93c5fd', marginBottom: '12px', textTransform: 'uppercase' }}>
                Current Listings
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {existingListings.map((listing) => (
                  <div
                    key={listing.id}
                    style={{
                      padding: '12px',
                      background: listing.status === 'active' ? 'rgba(34, 211, 238, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                      border: `1px solid ${listing.status === 'active' ? 'rgba(34, 211, 238, 0.3)' : 'rgba(107, 114, 128, 0.3)'}`,
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Tag style={{ width: 14, height: 14, color: listing.status === 'active' ? '#22D3EE' : '#6b7280' }} />
                        <span style={{ fontWeight: '600', color: listing.status === 'active' ? '#22D3EE' : '#9ca3af', fontSize: '0.875rem' }}>
                          {listing.platform}
                        </span>
                        <span style={{
                          fontSize: '0.65rem',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: listing.status === 'active' ? 'rgba(34, 211, 238, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                          color: listing.status === 'active' ? '#22D3EE' : '#9ca3af',
                          textTransform: 'uppercase'
                        }}>
                          {listing.status}
                        </span>
                      </div>
                      {listing.price && (
                        <div style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: '600' }}>
                          ${parseFloat(listing.price).toFixed(2)}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleToggleStatus(listing)}
                        style={{
                          padding: '6px 12px',
                          background: listing.status === 'active' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                          border: `1px solid ${listing.status === 'active' ? '#EF4444' : '#22C55E'}`,
                          borderRadius: '4px',
                          color: listing.status === 'active' ? '#EF4444' : '#22C55E',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        {listing.status === 'active' ? 'End' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteListing(listing.id, listing.platform)}
                        style={{
                          padding: '6px',
                          background: 'rgba(239, 68, 68, 0.2)',
                          border: '1px solid #EF4444',
                          borderRadius: '4px',
                          color: '#EF4444',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Trash2 style={{ width: 14, height: 14 }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Listings Form */}
          {availablePlatforms.length > 0 && (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#93c5fd', marginBottom: '-8px', textTransform: 'uppercase' }}>
                Add to Marketplaces
              </h3>

              {/* Marketplaces - Multi Select */}
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '12px',
                    color: '#93c5fd',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                >
                  Select Marketplaces
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {availablePlatforms.map((platform) => (
                    <button
                      key={platform}
                      type="button"
                      onClick={() => togglePlatform(platform)}
                      style={{
                        padding: '8px 16px',
                        background: selectedPlatforms.includes(platform)
                          ? 'rgba(59, 130, 246, 0.2)'
                          : 'rgba(31, 41, 55, 0.5)',
                        border: selectedPlatforms.includes(platform)
                          ? '2px solid #3b82f6'
                          : '1px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: '6px',
                        color: selectedPlatforms.includes(platform) ? '#93c5fd' : '#9ca3af',
                        fontSize: '0.875rem',
                        fontWeight: selectedPlatforms.includes(platform) ? '600' : '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {platform}
                    </button>
                  ))}
                </div>
                {selectedPlatforms.length > 0 && (
                  <p style={{ marginTop: '8px', fontSize: '0.75rem', color: '#6b7280' }}>
                    Selected: {selectedPlatforms.join(', ')}
                  </p>
                )}
              </div>

              {/* Listing ID */}
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#93c5fd',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                >
                  Listing ID (Optional)
                </label>
                <input
                  type="text"
                  value={listingId}
                  onChange={(e) => setListingId(e.target.value)}
                  placeholder="e.g., 123456789"
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: '#1f2937',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '6px',
                    color: '#e5e7eb',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Price */}
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#93c5fd',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                >
                  Listed Price
                </label>
                <div style={{ position: 'relative' }}>
                  <DollarSign
                    style={{
                      position: 'absolute',
                      left: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 16,
                      height: 16,
                      color: '#6b7280'
                    }}
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '10px 10px 10px 36px',
                      background: '#1f2937',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      color: '#e5e7eb',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#93c5fd',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                >
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: '#1f2937',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '6px',
                    color: '#e5e7eb',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="ended">Ended</option>
                </select>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px', paddingTop: '12px' }}>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'transparent',
                    border: '1px solid rgba(156, 163, 175, 0.3)',
                    borderRadius: '6px',
                    color: '#9ca3af',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || selectedPlatforms.length === 0}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: (loading || selectedPlatforms.length === 0) ? 'not-allowed' : 'pointer',
                    opacity: (loading || selectedPlatforms.length === 0) ? 0.5 : 1
                  }}
                >
                  {loading ? 'Saving...' : 'Add Listings'}
                </button>
              </div>
            </form>
          )}

          {availablePlatforms.length === 0 && existingListings.length > 0 && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '0.875rem' }}>
              All marketplaces have been listed. Use the controls above to manage them.
            </div>
          )}
        </div>
      </div>
    </>
  )
}
